<template>
  <div class="manage-assets" :class="platform" data-t="manage-assets">
    <p v-if="isDesktop" class="title">{{ t('payments.manageAssets.title') }}</p>
    <StDrawerHeader
      v-else
      :title="t('payments.manageAssets.title')"
      @close="emit('close')"
    />
    <p class="description">
      {{ t('payments.manageAssets.customizeCurrencies') }}
    </p>
    <StTabs
      v-if="isShowTabs"
      v-model="activeTab"
      :tabs="tabs"
      type="separate"
      class="manage-assets-tabs"
      data-t="manage-assets-tabs"
      style="overflow: visible"
    />
    <StScrolledShadowContainer :is-scrolled="isScrolled" />
    <div ref="account-list" class="account-list">
      <AccountItem
        v-for="account in displayedAccounts"
        :key="account.id"
        :account
        :disabled="isLastAccountVisible"
        data-t="manage-assets-account"
        @update-visible="updateVisible"
      />
    </div>
    <SiteAvailableCrypto class="available-crypto" background="primary" />
  </div>
</template>

<script setup lang="ts">
import { useAccountsStore } from '../../stores/useAccountsStore'
import AccountItem from './parts/AccountItem.vue'

const emit = defineEmits<{
  close: []
}>()

const { t } = useI18n()
const { platform, isDesktop } = usePlatform()

const wrapper = useTemplateRef('account-list')
const { isScrolled } = useScrolled({ element: wrapper })

const { realAccountsAvailiableForAccount: accounts } = useAllowedAssets()
const { getAccountList } = useAccountsStore()

const activeTab = ref('fiat')
const tabs = computed(() => [
  {
    id: 'fiat',
    label: t('payments.manageAssets.cash'),
  },
  {
    id: 'crypto',
    label: t('payments.manageAssets.crypto'),
  },
])

const isFiatAccountsExist = computed(() => !!accounts.value.fiat.length)
const isCryptoAccountsExist = computed(() => !!accounts.value.crypto.length)

const isShowTabs = computed(
  () => isFiatAccountsExist.value && isCryptoAccountsExist.value,
)

const displayedAccounts = computed<ExtendedUserAccount[]>(() => {
  if (!isShowTabs.value) {
    return isFiatAccountsExist.value
      ? accounts.value.fiat
      : accounts.value.crypto
  }
  if (activeTab.value === 'fiat') {
    return accounts.value.fiat
  }
  return accounts.value.crypto
})

const isLastAccountVisible = computed(() => {
  if (!isShowTabs.value) {
    return (
      displayedAccounts.value.filter((account) => account.isVisible).length ===
      1
    )
  }
  return (
    accounts.value.fiat.filter((account) => account.isVisible).length +
      accounts.value.crypto.filter((account) => account.isVisible).length ===
    1
  )
})

function updateVisible({ id, value }: { id: number; value: boolean }) {
  // @ts-expect-error новый роут
  const { error } = useStFetch('/account/update', {
    method: 'post',
    body: {
      accountId: id,
      isVisible: value,
    },
  })
  if (!error.value) {
    getAccountList()
  }
}
</script>

<style scoped>
.manage-assets {
  display: flex;
  flex-direction: column;

  max-width: 596px;
  height: 640px;
  padding: var(--spacing-300) 0;
}

.title {
  margin: 0;
  margin-bottom: var(--spacing-100);
  padding: 0 var(--spacing-300);
  font: var(--desktop-text-2xl-semibold);
}

.description {
  margin: 0;
  margin-bottom: var(--spacing-200);
  padding: 0 var(--spacing-300);

  font: var(--desktop-text-sm-medium);
  color: var(--text-secondary);
}

.manage-assets-tabs {
  padding: 0 var(--spacing-300) var(--spacing-200);
}

.account-list {
  overflow: hidden scroll;
  display: flex;
  flex-direction: column;
  padding: 0 var(--spacing-300);
}

.available-crypto {
  padding: 0 var(--spacing-200);
}

.mobile {
  &.manage-assets {
    height: 100%;
    padding: 0 0 var(--spacing-200);
  }

  .title {
    margin-bottom: var(--spacing-050);
    padding: 0 var(--spacing-200);
    font: var(--mobile-title-2-semibold);
  }

  .description {
    margin-bottom: var(--spacing-150);
    padding: 0 var(--spacing-200);
    font: var(--mobile-text-content-regular);
  }

  .manage-assets-tabs {
    padding: 0 var(--spacing-200) var(--spacing-150);
  }

  .account-list {
    padding: 0 var(--spacing-200);
  }
}
</style>
